import { titleArtPath } from '@goodbuild/rules/title-art';
import { titleMediaPath, titleMediaPosterPath } from '@goodbuild/rules/title-media';
import { formatMoney } from '@goodbuild/rules/format';
import type {
  DisplayPrice,
  StoreDescription,
  StoreLink,
  StoreListing,
  TitleMediaItem,
} from '@api/views';
import type { TitleArtKind } from '@goodbuild/rules/contracts';
import type { CapsuleTitle } from './ui';
import { apiOrigin, useApi } from './api';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from './api';

/**
 * The catalogue, in the shape the capsule components want.
 *
 * **The storefront's `lib/capsules.ts`, moved.** It existed there because `/`
 * and `/browse` drew the shelf the same way in two places — same price pick,
 * same nulls, same badge — and differed only in their limit. There is one page
 * now, at `/`, which is the end that argument was always heading towards. That was
 * survivable while a capsule had four fields and stopped being survivable when
 * in-house disclosure was added: the version of this mapping that forgets the
 * badge is not a broken page, it is a page that renders a GOODBUILD title as
 * though a third-party studio made it, on a store whose entire argument is that
 * its numbers are not massaged.
 *
 * The reason holds and the file moved with the pages. What changed is where the
 * rows come from: the storefront queried D1 in-process, and these pages run in
 * a browser, so it is `GET /v1/store` and the prices and the art arrive folded
 * into the same response. See `apps/api/src/routes/catalogue.ts` for why they
 * are folded rather than fetched per capsule.
 */

/*
  `HOUSE_BADGE` was here — the string `Our game` — and it is gone.

  It marked a title GOODBUILD made rather than signed, on a store where some
  titles were somebody else's. It was disclosure: the page put a description the
  seller wrote beside numbers the seller could not touch, and a reader weighing
  the first half was entitled to know we were the seller.

  There is no other kind of title. GOODBUILD makes the games it sells and signs
  nobody, so a badge on every capsule saying `Our game` distinguishes nothing —
  it is the wordmark in the bar repeated once per card, and it implies a
  contrast the catalogue does not contain.
*/

/**
 * The label on a title that is listed but not out yet (#138).
 *
 * A constant because the capsule, the hero and a test all say it: the version
 * of this string that reads
 * `Coming Soon` on one surface and `Coming soon` on another is nobody's job to
 * notice.
 */
export const COMING_SOON_LABEL = 'Coming soon';

/**
 * Whether a listing is something a visitor can buy today.
 *
 * The store lists two release states now, and everything that differs between
 * them hangs off this: the price, the buy action, the label in its place. Kept
 * beside `toCapsule` rather than inlined at each of them, because the version
 * where one surface tests `=== 'released'` and another tests
 * `!== 'coming_soon'` is the version that shows a price for a delisted title.
 */
export function isOnSale(listing: Pick<StoreListing, 'releaseState'>): boolean {
  return listing.releaseState === 'released';
}

/** One listing, with the prices and the filled art slots the API folded in. */
export interface ShelfEntry {
  listing: StoreListing;
  prices: DisplayPrice[];
  art: TitleArtKind[];
}

export interface Shelf {
  entries: ShelfEntry[];
  count: number;
}

/** One store page: the listing, its prices, its art, and what the game is. */
export interface StorePage {
  listing: StoreListing;
  prices: DisplayPrice[];
  art: TitleArtKind[];
  /** What the game is. Null on a title nobody has written one for yet. */
  description: StoreDescription | null;
  /** Where else it is sold. Empty on most titles. */
  storeLinks: StoreLink[];
  /** The screenshots and trailers, gallery order. Empty on most titles. */
  media: TitleMediaItem[];
}

export function titleMediaUrl(productId: string, mediaId: string): string {
  return `${apiOrigin()}${titleMediaPath(productId, mediaId)}`;
}

export function titleMediaPosterUrl(productId: string, mediaId: string): string {
  return `${apiOrigin()}${titleMediaPosterPath(productId, mediaId)}`;
}

/**
 * Where a browser fetches one slot of a title's art from.
 *
 * The API's public content route, prefixed with the origin it is on. Core
 * returns a path and knows nothing about hosts — see `titleArtPath`.
 *
 * Returns null for a slot the title has not filled, which is most slots on most
 * titles. Everything downstream degrades to the art-pending plate.
 */
export function artUrlFor(
  slots: readonly TitleArtKind[] | undefined,
  productId: string,
  kind: TitleArtKind,
): string | null {
  return slots?.includes(kind) ? `${apiOrigin()}${titleArtPath(productId, kind)}` : null;
}

/**
 * The view model for one capsule.
 *
 * EUR is the store's default display currency; anything else is better than
 * "Not priced yet" when a title happens not to carry one.
 */
export function toCapsule(entry: ShelfEntry): CapsuleTitle {
  const { listing, prices, art } = entry;
  const money = prices.find((price) => price.currency === 'EUR') ?? prices[0];
  const onSale = isOnSale(listing);

  return {
    slug: listing.slug,
    name: listing.name,
    studio: listing.developerName,
    /*
      No price on anything that is not for sale (#139).

      The API already withholds them — see `shelfFor` in
      `apps/api/src/routes/catalogue.ts`, which is the boundary that matters
      because it is the one a curious visitor can read. This is the second half
      of the same rule, on the near side of the wire, so that a capsule built
      from a response that somehow carries a price still does not draw one.
    */
    price: onSale && money ? formatMoney(money.currency, money.amountMinor) : null,
    /*
      And what goes in the price's place instead. `Coming soon` for a title we
      have announced; nothing for a released one, which shows its price.
    */
    releaseLabel: listing.releaseState === 'coming_soon' ? COMING_SOON_LABEL : null,
    /*
      The three shapes a capsule can be drawn at.

      Steam's main capsule for a landscape card, its library capsule for a
      portrait one and its library hero for the full-bleed image — the exact
      files a studio already has. Any of them may be null, and the components
      degrade one at a time: no landscape art is the art-pending plate, no
      portrait art letterboxes the landscape one, and no hero art is a hero with
      no picture behind its text.
    */
    artUrl: artUrlFor(art, listing.id, 'capsule_main'),
    portraitArtUrl: artUrlFor(art, listing.id, 'library_capsule'),
    heroArtUrl: artUrlFor(art, listing.id, 'library_hero'),
    pageBackgroundUrl: artUrlFor(art, listing.id, 'page_background'),
    logoUrl: artUrlFor(art, listing.id, 'logo'),
    // Live viewer counts are not modelled on a listing yet. Null renders no
    // badge rather than inventing one.
    liveViewers: null,
  };
}

/*
  `partitionShelf` was here, and #141 removed it.

  It split a shelf into what we made and what we published for somebody else,
  and the front page drew a rail from each half. GOODBUILD publishes nobody, so
  the second half is empty by construction — a partition with one side is not a
  partition, and a caller holding two arrays where one can never be filled is a
  shape that invites somebody to put a heading over the gap.

  `BrowseRoute` maps the entries straight through `toCapsule`, and the
  `Our game` badge that survived the first pass went too — see the note where
  `HOUSE_BADGE` used to be.
*/

/**
 * Every approved title the store lists — released, then coming soon.
 *
 * The limit is part of the query key, so two callers asking for different
 * lengths hold separate cached copies rather than one overwriting the other
 * with a shorter list. Only one caller asks today — the catalogue at `/`, with
 * no limit — since the twelve-title front page was folded into it.
 */
export function useShelf(limit?: number): UseQueryResult<Shelf, ApiError> {
  const query = limit === undefined ? '' : `?limit=${limit}`;
  return useApi<Shelf>(['store', 'shelf', limit ?? 'all'], `/store${query}`);
}

/** One store page, by slug. */
export function useStorePage(slug: string): UseQueryResult<StorePage, ApiError> {
  return useApi<StorePage>(['store', slug], `/store/${encodeURIComponent(slug)}`, {
    enabled: slug !== '',
  });
}
